import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const Stat = ({ value, label }) => (
  <div style={{ minWidth: 140 }}>
    <div style={{ fontSize: 44, fontWeight: 300, letterSpacing: "-.02em", color: "var(--v-text)" }}>{value}</div>
    <div style={{ fontSize: 11, marginTop: 6, color: "var(--v-text-2)", textTransform: "uppercase", letterSpacing: ".14em" }}>{label}</div>
  </div>
);

const Capability = ({ overline, title, children }) => (
  <div style={{ borderTop: "1px solid var(--v-border)", padding: "32px 0" }}>
    <div className="overline" style={{ color: "var(--v-accent)" }}>{overline}</div>
    <h3 style={{ fontSize: 26, marginTop: 8, letterSpacing: "-.01em" }}>{title}</h3>
    <p style={{ color: "var(--v-text-2)", marginTop: 12, maxWidth: 760, lineHeight: 1.65, fontSize: 14 }}>{children}</p>
  </div>
);

export default function About() {
  return (
    <div className="v-container" style={{ paddingTop: 80, paddingBottom: 140, maxWidth: 1080 }} data-testid="about-page">
      <Seo
        path="/about"
        title="About Verdict — built by Therefore Design, Mumbai"
        description="Verdict is built by Therefore Design, a Mumbai-based branding studio with 15+ years of packaging strategy experience across 1,000+ packaging systems and 200+ brands. Learn why we built an AI-powered packaging intelligence platform."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            url: "https://verdict.thereforedesign.co.in/about",
            name: "About Verdict",
            about: { "@id": "https://verdict.thereforedesign.co.in/#organization" },
          },
        ]}
      />
      <div className="overline" style={{ color: "var(--v-accent)" }}>About Verdict</div>
      <h1 style={{ fontSize: 56, marginTop: 14, lineHeight: 1.04, letterSpacing: "-.02em" }}>
        Built by the studio that lives inside Indian retail aisles.
      </h1>
      <p style={{ color: "var(--v-text-2)", marginTop: 22, maxWidth: 720, lineHeight: 1.7, fontSize: 17 }}>
        Verdict is a Human-in-the-Loop (HITL) AI-powered Packaging Intelligence Platform by <b style={{ color: "var(--v-text)" }}>Therefore Design</b> — a Mumbai-based
        brand & packaging studio that has spent the last two decades helping
        Indian FMCG, F&amp;B, beauty and lifestyle brands win at the shelf,
        on the marketplace, and in the kirana.
      </p>

      <div style={{ marginTop: 56, display: "flex", flexWrap: "wrap", gap: 40 }} data-testid="about-stats">
        <Stat value="20+" label="Years of design practice" />
        <Stat value="200+" label="Brands shipped" />
        <Stat value="7" label="Dimensions in our audit framework" />
        <Stat value="3" label="Continents launched into" />
      </div>

      <div style={{ marginTop: 80 }}>
        <Capability overline="Authority" title="The audit framework is the studio's IP — not a generic AI rubric.">
          The seven dimensions Verdict scores against — Brand alignment, E-commerce
          shelf, Aisle resiliency, Traditional retail in India, Distinctiveness,
          Information hierarchy, and Audience-fit — are the same lenses our senior
          strategists use in client workshops. The LLM inside Verdict applies them;
          it does not invent them.
        </Capability>

        <Capability overline="Indian retail nuance" title="Built for a market the global tools quietly fail at.">
          Modern trade ≠ kirana ≠ Q-commerce thumbnail. A pack that wins on Blinkit
          can disappear on a Reliance Smart shelf, and a pack that screams at the
          neighbourhood store can read as cheap on Nykaa. Verdict scores each lens
          separately so designers can make trade-offs intentionally instead of
          shipping a pack that "looks good on the cutting mat" and dies in the cart.
        </Capability>

        <Capability overline="Strategic depth" title="A second opinion from a senior creative director, on demand.">
          Most agencies bring in a senior creative director for a 90-minute pack
          critique. Verdict gives studios and brand teams that same critique in
          three minutes — structured, scored, comparison-ready, and exportable as
          a custom-branded PDF you can share with clients or your CEO.
        </Capability>

        <Capability overline="Built for designers, not gatekeepers" title="Iterate without bottlenecks.">
          Designers can compare up to four pack variations side-by-side, get a
          per-dimension recommendation, and run as many audits as they need —
          gated only by transparent audit credits, never by anyone's calendar.
          The strategic IP belongs to Therefore. The iteration speed belongs to you.
        </Capability>
      </div>

      <div style={{ marginTop: 90, padding: "44px 36px", background: "var(--v-bg-2)", border: "1px solid var(--v-border)" }}>
        <div className="overline" style={{ color: "var(--v-accent)" }}>For studios + brand teams</div>
        <h2 style={{ fontSize: 32, marginTop: 8, letterSpacing: "-.01em" }}>
          Want Therefore Design to actually do the redesign?
        </h2>
        <p style={{ color: "var(--v-text-2)", marginTop: 14, maxWidth: 640, lineHeight: 1.65, fontSize: 15 }}>
          Verdict gives you a diagnosis. If you want a treatment — full identity
          + packaging system, with the strategy embedded — write to the studio at
          {" "}<a href="mailto:info@thereforedesign.co.in" style={{ color: "var(--v-accent)" }}>info@thereforedesign.co.in</a>{" "}
          or browse our work at{" "}
          <a href="https://www.thereforedesign.co.in" target="_blank" rel="noreferrer" style={{ color: "var(--v-accent)" }}>thereforedesign.co.in</a>.
        </p>
        <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Link to="/pricing" className="v-btn" style={{ background: "var(--v-accent)", color: "#0A0A0A" }} data-testid="about-pricing-cta">
            See pricing →
          </Link>
          <Link to="/support" className="v-btn v-btn-outline" data-testid="about-support-cta">
            Talk to us
          </Link>
        </div>
      </div>
    </div>
  );
}
